<template>
  <section class="section">
    <div class="columns">
      <div class="column is-half container">
        <b-field
          type="is-blue"
          expanded
          grouped
        >
          <b-input
            expanded
            :disabled="shouldDisplayLoading"
            :loading="shouldDisplayLoading"
            v-model="searchText"
            icon-pack="fas"
            icon="search"
            :placeholder="$t('Search')"
            data-test-id="notificationGroupsSearch"
          />
        </b-field>
      </div>
    </div>
    <div class="container">
      <notificationGroupDetails
        :notificationGroups="filteredGroupList"
        :showRelatedItems="true"
        @member-count-clicked="memberSelected"
        @shop-count-clicked="shopSelected"
      />
    </div>
    <b-modal
      v-model="isVisibleRelatedMembers"
      :can-cancel="true"
      has-modal-card
      aria-modal
      width="auto"
    >
      <memberDetailsModal
        :membersList="membersAdministrationListDetails"
        :loading="shouldDisplayLoading"
        :titleString="titleString"
        @close="closeRelatedMembersModal"
      />
    </b-modal>
    <b-modal
      v-model="isVisibleRelatedShops"
      :can-cancel="true"
      has-modal-card
      width="auto"
      aria-modal
    >
      <shopDetailsModal
        :shopsList="shopsAdministrationListDetails"
        :loading="shouldDisplayLoading"
        :titleString="titleString"
        @close="closeRelatedShopsModal"
      />
    </b-modal>
  </section>
</template>
<script lang="ts">
// @ts-nocheck - Disable TypeScript checking during Vue 2 -> Vue 3 migration
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/app'
import shopsService from '@/services/shopsService'
import membersService from '@/services/membersService'
import notificationService from '@/services/notificationService'
import memberDetailsModal from '@/components/modals/memberDetailsModal.vue'
import notificationGroupDetails from '@/components/notifications/notificationGroupDetails.vue'
import shopDetailsModal from '@/components/modals/shopDetailsModal.vue'

export default defineComponent({
  name: 'NotificationGroupAdministration',
  components: {
    notificationGroupDetails,
    memberDetailsModal,
    shopDetailsModal
  },
  setup() {
    // Composables
    const { t } = useI18n()
    const store = useAppStore()

    // Reactive data - using compatible initialization
    const searchText = ref('')
    const selectedMember = ref(Object.create(null))
    const relatedMemberList = ref(new Array())
    const isVisibleRelatedMembers = ref(false)
    const isVisibleRelatedShops = ref(false)
    const relatedShops = ref(new Array())
    const selectedGroup = ref(Object.create(null))

    // Computed properties
    const notificationGroupList = computed(() => store.getNotificationGroups)
    const memberList = computed(() => store.getMemberList)
    const shopList = computed(() => store.getAllowedShopsList)
    const strategiesList = computed(() => store.getStrategies)
    const chainsList = computed(() => store.getChains)

    const titleString = computed(() => {
      return selectedGroup.value && selectedGroup.value.groupId
        ? `${t('NotificationGroup')} ${selectedGroup.value.groupId} ${selectedGroup.value.groupName}`
        : ''
    })

    const shouldDisplayLoading = computed(() => {
      return (
        !Array.isArray(notificationGroupList.value) ||
        !notificationGroupList.value.length
      )
    })

    const groupsAdministrationListDetails = computed(() => {
      if (!notificationGroupList.value || !memberList.value) {
        return []
      }
      
      try {
        return notificationService.prepareNotificationDetails(
          notificationGroupList.value,
          memberList.value
        )
      } catch (error) {
        console.error('Error preparing notification details:', error)
        return []
      }
    })

    const filteredGroupList = computed(() => {
      if (!searchText.value.trim()) {
        return groupsAdministrationListDetails.value
      }

      const searchTerms = searchText.value
        .toUpperCase()
        .split(/\s+/)
        .filter(term => term.length > 0)

      return groupsAdministrationListDetails.value.filter(group => 
        searchTerms.every(phrase =>
          String(group.groupName || '')
            .concat(String(group.groupId || ''))
            .concat(String(group.groupDescription || ''))
            .toUpperCase()
            .includes(phrase)
        )
      )
    })

    const relatedMembersModalReady = computed(() => {
      return (
        Array.isArray(notificationGroupList.value) &&
        Array.isArray(memberList.value) &&
        Array.isArray(strategiesList.value)
      )
    })

    const relatedShopsModalReady = computed(() => {
      return (
        Array.isArray(notificationGroupList.value) &&
        Array.isArray(memberList.value) &&
        Array.isArray(chainsList.value) &&
        Array.isArray(shopList.value)
      )
    })

    const membersAdministrationListDetails = computed(() => {
      if (!relatedMembersModalReady.value) {
        return []
      }
      
      try {
        return membersService.prepareMembersDetails(
          relatedMemberList.value,
          notificationGroupList.value,
          strategiesList.value
        )
      } catch (error) {
        console.error('Error preparing members details:', error)
        return []
      }
    })

    const shopsAdministrationListDetails = computed(() => {
      if (!relatedShopsModalReady.value) {
        return []
      }
      
      try {
        return shopsService.prepareShopsData(
          relatedShops.value,
          notificationGroupList.value,
          memberList.value,
          chainsList.value
        )
      } catch (error) {
        console.error('Error preparing shops data:', error)
        return []
      }
    })

    // Methods
    function memberSelected(groupId) {
      if (!relatedMembersModalReady.value) {
        return
      }

      const foundGroup = notificationGroupList.value?.find(
        g => g.groupId === groupId
      )
      
      if (foundGroup) {
        selectedGroup.value = foundGroup
      }

      const foundMember = groupsAdministrationListDetails.value.find(
        x => x.groupId === groupId
      )
      
      if (foundMember) {
        selectedMember.value = foundMember
        // Handle members property that can be number or array
        if (Array.isArray(foundMember.members)) {
          relatedMemberList.value.length = 0 // Clear array
          foundMember.members.forEach(member => relatedMemberList.value.push(member))
        } else {
          relatedMemberList.value.length = 0 // Clear array
        }
        
        if (relatedMemberList.value.length > 0) {
          isVisibleRelatedMembers.value = true
        }
      }
    }

    function shopSelected(groupId) {
      if (!relatedShopsModalReady.value) {
        return
      }

      const foundGroup = notificationGroupList.value?.find(
        g => g.groupId === groupId
      )
      
      if (foundGroup) {
        selectedGroup.value = foundGroup
      }

      const relatedNotificationGroup = notificationGroupList.value?.find(
        g => g.groupId === groupId
      )
      
      if (relatedNotificationGroup && shopList.value) {
        const relatedShopIds = relatedNotificationGroup.shopIds || []
        const filteredShops = shopList.value.filter(shop =>
          relatedShopIds.includes(shop.shopId)
        )
        
        // Clear and repopulate array
        relatedShops.value.length = 0
        filteredShops.forEach(shop => relatedShops.value.push(shop))
        
        if (relatedShops.value.length > 0) {
          isVisibleRelatedShops.value = true
        }
      }
    }

    function closeRelatedMembersModal() {
      isVisibleRelatedMembers.value = false
    }

    function closeRelatedShopsModal() {
      isVisibleRelatedShops.value = false
    }

    // Lifecycle
    onMounted(async () => {
      try {
        // Load data sequentially to avoid type issues
        if (!notificationGroupList.value) {
          await notificationService.getNotificationGroups()
        }
        
        if (!memberList.value) {
          await membersService.getMembersList()
        }
        
        if (!shopList.value) {
          await shopsService.getAllowedShopsList()
        }
        
        if (!strategiesList.value) {
          notificationService.getStrategies()
        }
        
        if (!chainsList.value) {
          await shopsService.getChains()
        }
      } catch (error) {
        console.error('Error loading data:', error)
      }
    })

    return {
      searchText,
      selectedMember,
      relatedMemberList,
      isVisibleRelatedMembers,
      isVisibleRelatedShops,
      relatedShops,
      selectedGroup,
      notificationGroupList,
      memberList,
      shopList,
      strategiesList,
      chainsList,
      titleString,
      shouldDisplayLoading,
      groupsAdministrationListDetails,
      filteredGroupList,
      membersAdministrationListDetails,
      shopsAdministrationListDetails,
      memberSelected,
      shopSelected,
      closeRelatedMembersModal,
      closeRelatedShopsModal
    }
  }
})
</script>
